import React, { useState, useEffect } from "react";
import { Avatar, Button, Layout, Menu, Tooltip } from "antd";
import {
  BankOutlined,
  CloudUploadOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TableOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserRoles, logout } from "../../api/auth";
import { getTransactionPlanAccess } from "../transactionPlan/transactionPlanPermissions";
import { isRouteDisabled } from "../../config/disabledModules";
import { SETUP_IMPORT_ENABLED } from "../../config/setupImport";
import { useTranslation } from "../../i18n/LanguageProvider";
import LanguageSwitcher from "../../i18n/LanguageSwitcher";
import ROUTE_PATHS from "../../routes/routePaths";
import { useOpenRoute } from "../../utils/openRouteUtils";

const { Sider } = Layout;

function getCurrentAuthInfo() {
  const token = localStorage.getItem("access_token");
  const payload = token ? JSON.parse(atob(token.split(".")[1] || "e30=")) : {};
  const roles = getUserRoles(token).map((r) => String(r).replace("ROLE_", ""));

  return {
    username:
      payload?.sub ||
      payload?.username ||
      payload?.preferred_username ||
      "Unknown",
    role: roles?.[0] || null,
  };
}

function AppSidebar({ collapsed, setCollapsed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const openRoute = useOpenRoute();
  const { username, role } = getCurrentAuthInfo();
  const { t } = useTranslation();
  const [openKeys, setOpenKeys] = useState([]);

  const isAdmin = role === "ADMIN";
  const isModerator = role === "MODERATOR";
  const isAuthor = role === "AUTHOR";

  const transactionPlanAccess = getTransactionPlanAccess();

  const items = [
    {
      key: "transaction-schedule-group",
      icon: <CreditCardOutlined />,
      label: t("nav.transactionSchedule"),
      allowed:
        (isAdmin || isModerator || isAuthor) &&
        !isRouteDisabled(ROUTE_PATHS.TRANSACTION_SCHEDULE),
      children: [
        {
          key: ROUTE_PATHS.TRANSACTION_SCHEDULE_DASHBOARD,
          icon: <DashboardOutlined />,
          label: t("nav.transactionSchedule"),
        },
        {
          key: ROUTE_PATHS.TRANSACTION_SCHEDULE,
          icon: <TableOutlined />,
          label: "Хуваарийн жагсаалт",
        },
      ],
    },
    {
      key: ROUTE_PATHS.TRANSACTION_PLAN,
      icon: <FileTextOutlined />,
      label: t("nav.transactionPlan"),
      allowed: transactionPlanAccess.canView,
    },
    {
      key: ROUTE_PATHS.MANAGE_INSTITUTIONS,
      icon: <WalletOutlined />,
      label: t("nav.balance"),
      allowed:
        (isAdmin || isModerator || isAuthor) &&
        !isRouteDisabled(ROUTE_PATHS.MANAGE_INSTITUTIONS),
    },
    {
      key: ROUTE_PATHS.TRANSACTION_DATA_TABLE,
      icon: <DatabaseOutlined />,
      label: t("nav.statements"),
      allowed:
        (isAdmin || isModerator || isAuthor) &&
        !isRouteDisabled(ROUTE_PATHS.TRANSACTION_DATA_TABLE),
    },
    {
      key: ROUTE_PATHS.ADMIN_INSTITUTIONS,
      icon: <BankOutlined />,
      label: t("nav.institutions"),
      allowed: isAdmin && !isRouteDisabled(ROUTE_PATHS.ADMIN_INSTITUTIONS),
    },
    {
      key: ROUTE_PATHS.USERS,
      icon: <TeamOutlined />,
      label: t("nav.users"),
      allowed: isAdmin,
    },
    // TEMPORARY initial-data setup tool - switch off in config/setupImport.js
    {
      key: ROUTE_PATHS.SETUP_IMPORT,
      icon: <CloudUploadOutlined />,
      label: t("nav.setupImport"),
      allowed: isAdmin && SETUP_IMPORT_ENABLED,
    },
  ].filter((item) => item.allowed);

  const handleLogout = () => {
    logout();
    navigate(ROUTE_PATHS.LOGIN, { replace: true });
  };

  const handleMenuClick = ({ key, domEvent }) => {
    if (typeof key === "string" && key.startsWith("/")) {
      openRoute(key, domEvent);
    }
  };

  useEffect(() => {
    if (
      openKeys.length === 0 &&
      location.pathname.startsWith("/transaction-schedule") &&
      !isRouteDisabled(ROUTE_PATHS.TRANSACTION_SCHEDULE)
    ) {
      setOpenKeys(["transaction-schedule-group"]);
    }
  }, [location.pathname, openKeys.length]);

  return (
    <Sider
      collapsible={false}
      collapsed={collapsed}
      width={240}
      collapsedWidth={80}
      theme="dark"
      className="!fixed left-0 top-0 z-30 h-screen bg-slate-950"
    >
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4">
          {!collapsed ? (
            <div className="min-w-0">
              <div className="truncate text-base font-semibold text-white">
                {t("app.title")}
              </div>
              <div className="truncate text-xs text-white/60">
                {t("app.subtitle")}
              </div>
            </div>
          ) : (
            <div className="mx-auto text-lg font-semibold text-white">STU</div>
          )}

          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((prev) => !prev)}
            className="!text-white hover:!text-white/80"
          />
        </div>

        <div className="shrink-0 border-b border-white/10 px-4 py-4">
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Avatar size="small" icon={<UserOutlined />} />
              <LanguageSwitcher placement="right" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Avatar size="small" icon={<UserOutlined />} />

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-white">
                  {username || "-"}
                </div>
                <div className="truncate text-[11px] uppercase tracking-wide text-white/60">
                  {role || "-"}
                </div>
              </div>

              <LanguageSwitcher />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-hidden py-2">
          <Menu
            theme="dark"
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[location.pathname]}
            onOpenChange={(keys) => setOpenKeys(keys)}
            items={items}
            onClick={handleMenuClick}
            className="app-sidebar-menu h-full border-none bg-transparent"
          />
        </div>

        <div className="shrink-0 border-t border-white/10 p-4">
          {collapsed ? (
            <Tooltip title={t("nav.logout")} placement="right">
              <Button icon={<LogoutOutlined />} danger block onClick={handleLogout} />
            </Tooltip>
          ) : (
            <Button icon={<LogoutOutlined />} danger block onClick={handleLogout}>
              {t("nav.logout")}
            </Button>
          )}
        </div>
      </div>
    </Sider>
  );
}

export default AppSidebar;